import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import React, { useEffect, useMemo } from "react";
import {
  DataList,
  FileLayout,
  FileLayoutKeys,
  FileLayoutMaintenancePageInitialState,
  usePageState,
} from ".";
import { useAppState } from "../../app/useAppState";
import {
  clearBackenddMessages,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";

import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {
  GetFileLayout,
  PutFileLayout,
  useGetDataKind,
  usePutDataKind,
} from "../../services/data-kinds";
import { useGetGeneralCodes } from "../../services/data-list";
import {
  InDefCommonFlagType,
  LayoutDerivationReasonType,
} from "../../util/general-code/enums/general-codes";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";
import _ from "lodash";

export const FileLayoutMaintenancePageContent = () => {
  const { resetUserError, showToast, showConfirmationDialog } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();

  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { fileLayoutList } = pageState.searchResult;

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getDataKindRequest, sendGetDataKindRequest] = useGetDataKind();
  const [putDataKindRequest, sendPutDataKindRequest] = usePutDataKind();

  const TEMPLATE_FILE_ID_URL =
    "spreadsheet-template/file-layout-maintenance.xlsx";

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: "File Layout Maintenance",
    }));
    sendGetGeneralCodesRequest({
      cdTypes: ["in_def_common_flag_type", "layout_derivation_reason_type"],
    });
    sendGetDataKindRequest({});
  };

  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === "in_def_common_flag_type",
      )?.cdList ?? [];
    const layoutDerivationReasonType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === "layout_derivation_reason_type",
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inDefCommonFlagType,
        layoutDerivationReasonType,
      },
    }));
  };
  const onGetDataKindResponded = () => {
    if (!getDataKindRequest) {
      return;
    }
    const fileLayoutList = [] as FileLayout[];
    getDataKindRequest.fileLayoutList.forEach((e, idx) => {
      const fileLayout = {} as FileLayout;
      fileLayout.rowNum = idx;
      fileLayout.addFlg = InDefCommonFlagType.NO;
      fileLayout.getFileLayout = e;
      fileLayout.errorColumnList = [];
      fileLayout.errorMessage = "";
      fileLayout.messageList = [];
      fileLayoutList.push(fileLayout);
    });
    setPageState((prevState) => ({
      ...prevState,
      isSaved: true,
      searchResult: {
        ...prevState.searchResult,
        fileLayoutList: fileLayoutList,
      },
    }));
  };

  const onPutDataKindResponded = () => {
    if (!putDataKindRequest) return;

    if (Array.isArray(putDataKindRequest)) {
      const currentrecords: FileLayout[] = spreadsheetState
        ? [...spreadsheetState.fileLayoutList]
        : [];
      const currentrecordsWithoutBackendError = currentrecords.map((row) =>
        clearBackenddMessages(row),
      );
      const newSpreadsheetRecordData: FileLayout[] =
        mappingErrorResponseToSpreadsheet(
          putDataKindRequest,
          currentrecordsWithoutBackendError,
          FileLayoutMaintenancePageInitialState.displayColumn,
        );

      if (!spreadsheetState) return;
      setSpreadsheetState({
        ...spreadsheetState,
        fileLayoutList: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetDataKindRequest({});
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetDataKindResponded, [getDataKindRequest]);
  useEffect(onPutDataKindResponded, [putDataKindRequest]);

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<{ fileLayoutList: FileLayout[]; dataList: DataList }>();

  const spreadsheetInitialData = useMemo(() => {
    return {
      fileLayoutList: fileLayoutList,
      dataList: pageState.dataList,
    };
  }, [fileLayoutList, pageState.dataList]);

  const isFileLayoutKey = (arg: any): arg is keyof FileLayout => {
    const fileLayoutKeys: (keyof GetFileLayout)[] = [
      "delFlg",
      "dtKindId",
      "ownCpntId",
      "fileId",
      "bsFileFlg",
      "frkRsnTypCd",
    ];
    return fileLayoutKeys.includes(arg);
  };

  /**
   * Spreadsheet cell selected.
   * @param event that is passed from spreadsheet.
   */
  const onCellSelected = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheetSelectedRowIndex: rowIndex,
    }));
  };

  /**
   * Spreadsheet delete icon clicked.
   * @param event that is passed from spreadsheet.
   */
  const onDeleteIconClicked = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    if (event.detail.name !== "deleteIcon") return;
    const newSpreadsheetState: FileLayout[] = spreadsheetState
      ? [...spreadsheetState.fileLayoutList]
      : [];
    const rowIndex: number = event.detail.cell?.data.index;
    newSpreadsheetState.splice(rowIndex, 1);

    setSpreadsheetState({
      ...spreadsheetState,
      fileLayoutList: newSpreadsheetState,
    });
  };

  const onCellEdited = (event: CustomEvent) => {
    if (event.detail.name === "on-cell-edited") return;
    const rowIndex: number = event.detail.cell.data.index;
    const value = event.detail.cell.value ?? "";
    const eventName: string = event.detail.name;

    if (isFileLayoutKey(eventName)) {
      const isChanged = event.detail.cell.row.isChanged;
      const copyFileLayoutList = JSON.parse(
        JSON.stringify(spreadsheetState?.fileLayoutList),
      );
      copyFileLayoutList[rowIndex]["getFileLayout"][eventName] = value;
      copyFileLayoutList[rowIndex]["isEdited"] = isChanged;
      const newState = JSON.parse(JSON.stringify(spreadsheetState));
      newState.fileLayoutList = copyFileLayoutList;
      setSpreadsheetState(newState);
      setPageState((prevState) => ({ ...prevState, isSaved: false }));
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newFilelayoutList = _.cloneDeep(
      spreadsheetState?.fileLayoutList,
    ) as FileLayout[];

    const colName: FileLayoutKeys = event.detail.name as FileLayoutKeys;
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      newFilelayoutList[rowIdx].getFileLayout[colName] = value;
      newFilelayoutList[rowIdx].isEdited = true;
    });

    setPageState((prevState) => ({ ...prevState, isSaved: false }));

    setSpreadsheetState({
      ...spreadsheetState,
      fileLayoutList: newFilelayoutList,
    });
  };

  const onAddRowClicked = () => {
    if (!spreadsheetState) return;
    const prevFileLayoutList = spreadsheetState?.fileLayoutList;
    const insertRowIndex: number =
      (pageState.spreadsheetSelectedRowIndex ?? 0) + 1;

    const newSpreadsheetState = spreadsheetState
      ? [...spreadsheetState.fileLayoutList]
      : [];
    const addSpreadsheetRow: FileLayout = {
      rowNum: prevFileLayoutList.length,
      addFlg: InDefCommonFlagType.YES,
      isEdited: true,
      getFileLayout: {
        delFlg: InDefCommonFlagType.NO,
        dtKindId: "",
        dtKindNameLclLang: "",
        dtKindNameEng: "",
        fileId: "",
        ownCpntId: "",
        bsFileFlg: InDefCommonFlagType.YES,
        frkRsnTypName: "",
        frkRsnTypCd: LayoutDerivationReasonType.ORIGIN,
        updateDateTime: "",
      },
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    };

    newSpreadsheetState.splice(insertRowIndex, 0, addSpreadsheetRow);
    setSpreadsheetState({
      ...spreadsheetState,
      fileLayoutList: [...newSpreadsheetState],
    });
  };

  /**
   * Footer
   */
  const FooterContent: React.FC = () => {
    const saveButtonClickedHandle = () => {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          const fileLayoutList = [] as PutFileLayout[];
          spreadsheetState?.fileLayoutList.forEach((e, index) => {
            if (e.isEdited) {
              let fileLayout = {} as PutFileLayout;
              fileLayout.delFlg = e.getFileLayout.delFlg;
              fileLayout.dtKindId = e.getFileLayout.dtKindId;
              fileLayout.fileId = e.getFileLayout.fileId;
              fileLayout.ownCpntId = e.getFileLayout.ownCpntId;
              fileLayout.bsFileFlg = e.getFileLayout.bsFileFlg;
              fileLayout.frkRsnTypCd = e.getFileLayout.frkRsnTypCd;
              fileLayout.updateDateTime = e.getFileLayout.updateDateTime;
              fileLayout.index = index;
              fileLayout.addFlag = e.addFlg;
              fileLayoutList.push(fileLayout);
            }
          });
          sendPutDataKindRequest({ fileLayoutList: fileLayoutList });
        },
      });
    };
    return (
      <FrVrfFooter>
        {/* Save Button */}
        <FrVrfButton
          content="Save"
          theme="primary"
          size="large"
          disabled={pageState.isSaved}
          onClicked={saveButtonClickedHandle}
          authActId="file_layout_maintenance.button.save"
        />
      </FrVrfFooter>
    );
  };

  return (
    <>
      <FrVrfTextDisplayField text="File Layout Maintenance" />
      <FrVrfButtonPanel>
        <FrVrfButton
          content="Add Row"
          size="medium"
          theme="secondary"
          onClicked={onAddRowClicked}
          authActId="file_layout_maintenance.button.add_row"
        />
      </FrVrfButtonPanel>
      <br />
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={TEMPLATE_FILE_ID_URL}
        onInitialized={initSpreadsheetState}
        onCellSelected={onCellSelected}
        onCellClicked={onDeleteIconClicked}
        onCellEdited={onCellEdited}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={800}
      />
      <br />
      {/* Footer */}
      <FooterContent />
    </>
  );
};
